import React, {CSSProperties, KeyboardEvent, ReactNode, useContext, useState} from 'react'

import {focusableClass, ensureFocusStyles} from '../styles.js'
import {TabChangeEvent, TabsContext} from './Tabs.js'
import {TabVariant, tabColors, tabSizes} from './styles.js'

ensureFocusStyles()

interface TabProps {
    value?: string
    children?: ReactNode
    style?: CSSProperties
    size?: 'sm' | 'md'
    disabled?: boolean
    /** Active instead of from the value of Tabs */
    active?: boolean
    /** Called instead of the onChange of Tabs */
    onClick?: (event: TabChangeEvent) => void
    /** Look of the tab instead of the variant of Tabs */
    variant?: TabVariant
}

/**
 * One tab in Tabs. It is active when its value is the value of Tabs (or the active prop), clicking it or pressing
 * Enter or Space on it calls the onChange of Tabs with its value.
 */
export default function Tab({value, children, style = {}, size = 'md', disabled, active, onClick, variant}: TabProps) {
    const tabs = useContext(TabsContext)
    const [hover, setHover] = useState(false)

    const isActive = active ?? (!!tabs && (tabs.value === value || !!tabs.values?.includes(value)))
    const highlighted = hover && !disabled
    const courier = (variant ?? tabs?.variant) === 'courier'

    const select = () => {
        if (disabled) {
            return
        }

        const handler = onClick || tabs?.onChange
        handler?.({target: {value}})
    }

    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter' || event.key === ' ') {
            // Space would scroll the page
            event.preventDefault()
            select()
        }
    }

    const defaultStyle: CSSProperties = {
        width: '100%',
        height: '100%',
        fontSize: typeof children === 'string' ? tabSizes.fontSize[size] : tabSizes.iconSize,
        fontWeight: tabSizes.fontWeight,
        letterSpacing: tabSizes.letterSpacing,
        background: highlighted ? tabColors.hover : 'transparent',
        color: disabled ? tabColors.textDisabled : isActive ? tabColors.textActive : tabColors.text,
        cursor: disabled ? 'default' : 'pointer',
        // The courier line lies over the bottom of the tab
        position: courier ? 'relative' : undefined,
        ...style
    }

    // The upper line shows hover and active, the lower one is the border of the row of tabs
    const upperLineStyle: CSSProperties = {
        width: '100%',
        height: tabSizes.lineHeight,
        background: isActive ? tabColors.lineActive : (highlighted ? tabColors.hover : 'transparent'),
        transition: tabSizes.transition
    }

    const lowerLineStyle: CSSProperties = {
        width: '100%',
        height: tabSizes.lineHeight,
        background: isActive ? tabColors.lineActive : tabColors.line,
        transition: tabSizes.transition
    }

    const courierLineStyle: CSSProperties = {
        position: 'absolute',
        bottom: 0,
        width: '100%',
        height: isActive ? tabSizes.courierLineHeightActive : tabSizes.courierLineHeight,
        background: tabColors.courierLine,
        transition: tabSizes.transition
    }

    const paddingX = courier ? 0 : tabSizes.paddingX[size]

    return (
        <div
            role='tab'
            aria-selected={isActive}
            aria-disabled={disabled || undefined}
            tabIndex={disabled ? -1 : 0}
            className={focusableClass}
            style={defaultStyle}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            onClick={select}
            onKeyDown={onKeyDown}
        >
            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: courier ? 'center' : undefined, height: '100%', paddingLeft: paddingX, paddingRight: paddingX}}>
                {children}
            </div>

            {courier ?
                <div style={courierLineStyle} /> :
                <>
                    <div style={upperLineStyle} />
                    <div style={lowerLineStyle} />
                </>
            }
        </div>
    )
}
